import { getColumns } from '@utils/columns';
import { shouldShowColumn } from '@utils/ratingVisibility';
import _ from 'lodash';

import { Column } from '../../columns/createColumn';
import {
    accelerationPedalRating,
    accelerationPedalRatingV2,
    cruiseControlRatingV2,
    cruiseControlRatingV3,
    electricAccelerationPedalRating,
    electricCruiseControlRating,
    electricForesightedDrivingPercentage,
    electricOverspeedRating,
    electricRecuperationRating,
    fuelForesightedDrivingPercentage,
    totalRatingV2,
    totalRatingV3,
} from '../../columns/percentage';
import { CalculationParams, defaultCalculationParams } from './../../features/settings/reducer';
import { replacements, tableColumns } from './columns';
import { DriverFuelType, UseCaseKey } from './driverAnalysisReducer';

const SOP_OVERVIEW_FUEL_COLUMNS_EXCLUSIVE = [
    'fuelForesightedDrivingPercentage',
    'fuelEfficiency',
    'drivingConsumption',
];

const SOP_OVERVIEW_ELECTRIC_COLUMNS_EXCLUSIVE = [
    'electricForesightedDrivingPercentage',
    'electricEfficiency',
    'electricAverageDrivingConsumption',
];

const overviewColumns = [
    'drivers',
    'vehicles',
    'tripStartEnd',
    'operationCondition',
    'totalRating',
    'fuelForesightedDrivingPercentage',
    'electricForesightedDrivingPercentage',
    'electricAverageDrivingConsumption',
    'drivingConsumption',
    'mileage',
    'averageWeight',
    'fuelEfficiency',
    'electricEfficiency',
];
const overviewSummaryColumns = [
    'totalRating',
    'drivingConsumption',
    'mileage',
    'averageWeight',
    'fuelEfficiency',
];

const harshRatings = ['harshAccelerationRating', 'harshBrakingRating'];

export const electricRatings = [
    'electricForesightedDrivingPercentage',
    'electricCruiseControlRating',
    'electricOverspeedRating',
];
export const electricSOPRatings = ['electricRecuperationRating'];

const excessiveIdlingRating = ['excessiveIdlingRating'];

const ratingColumns = [
    'drivers',
    'vehicles',
    'tripStartEnd',
    'operationCondition',
    'totalRating',
    'coastingRating',
    'accelerationPedalRating',
    'brakingRating',
    'cruiseControlRating',
    'overspeedRating',
    ...harshRatings,
    ...excessiveIdlingRating,
    'electricRecuperationRating',
];
const ratingSummaryColumns = [
    'totalRating',
    'coastingRating',
    'accelerationPedalRating',
    'brakingRating',
    'cruiseControlRating',
    'overspeedRating',
    ...harshRatings,
    ...excessiveIdlingRating,
    'electricRecuperationRating',
];

export type UseCaseConfig = {
    key: string;
    columns: Column[];
    summaryColumns: Column[];
};

export const useCaseConfig: UseCaseConfig[] = [
    {
        key: 'overview',
        columns: getColumns(overviewColumns, tableColumns),
        summaryColumns: getColumns(overviewSummaryColumns),
    },
    {
        key: 'ratings',
        columns: getColumns(ratingColumns, tableColumns),
        summaryColumns: getColumns(ratingSummaryColumns),
    },
];

type Settings = {
    calculationParams: CalculationParams;
    shouldUseV2Ratings: boolean;
    shouldShowHarshKPIs: boolean;
    shouldShowExcessiveKPIs: boolean;
    shouldUseV3Ratings: boolean;
    selectedFuelType: DriverFuelType;
    shouldConsiderElectricParams?: boolean; // ft_treeFilter feature flag - TODO: Remove when flag is permanent
};
export const getUseCaseConfigForKey = (
    useCaseKey: UseCaseKey,
    {
        shouldUseV2Ratings,
        calculationParams,
        shouldShowHarshKPIs,
        shouldShowExcessiveKPIs,
        shouldUseV3Ratings,
        selectedFuelType,
        shouldConsiderElectricParams = true, // ft_treeFilter - TODO: Remove when flag is permanent
    }: Settings = {
        calculationParams: defaultCalculationParams,
        shouldUseV2Ratings: true,
        shouldShowHarshKPIs: false,
        shouldShowExcessiveKPIs: false,
        shouldUseV3Ratings: false,
        selectedFuelType: DriverFuelType.ALL,
        shouldConsiderElectricParams: true,
    },
): UseCaseConfig | undefined => {
    const config = _.find(useCaseConfig, ['key', useCaseKey]);

    const shouldExcludeOverviewSOPColumn = (column: Column, selectedFuelType: DriverFuelType) => {
        if (column.key === 'totalRating') return true;

        switch (selectedFuelType) {
            case DriverFuelType.FUEL:
                return [...SOP_OVERVIEW_ELECTRIC_COLUMNS_EXCLUSIVE].includes(column.key);
            case DriverFuelType.ELECTRIC:
                return [...SOP_OVERVIEW_FUEL_COLUMNS_EXCLUSIVE].includes(column.key);
            default:
                return (
                    [
                        ...SOP_OVERVIEW_FUEL_COLUMNS_EXCLUSIVE,
                        ...SOP_OVERVIEW_ELECTRIC_COLUMNS_EXCLUSIVE,
                    ].includes(column.key) &&
                    ![
                        'electricForesightedDrivingPercentage',
                        'fuelForesightedDrivingPercentage',
                    ].includes(column.key)
                );
        }
    };

    if (config) {
        return {
            ...config,
            summaryColumns: config.summaryColumns.reduce((acc: Column[], column: Column) => {
                if (
                    !shouldShowColumn(
                        column,
                        calculationParams,
                        selectedFuelType,
                        shouldShowHarshKPIs,
                        shouldShowExcessiveKPIs,
                        shouldConsiderElectricParams,
                    )
                ) {
                    return acc;
                }

                if (shouldUseV3Ratings) {
                    if (column.key === 'accelerationPedalRating') {
                        acc.push(accelerationPedalRating);
                        return acc;
                    }

                    if (column.key === 'cruiseControlRating') {
                        acc.push(cruiseControlRatingV3);
                        return acc;
                    }

                    if (column.key === 'totalRating') {
                        acc.push(totalRatingV3);
                        return acc;
                    }
                } else if (shouldUseV2Ratings) {
                    if (column.key === 'accelerationPedalRating') {
                        acc.push(accelerationPedalRatingV2);
                        return acc;
                    }

                    if (column.key === 'cruiseControlRating') {
                        acc.push(cruiseControlRatingV2);
                        return acc;
                    }

                    if (column.key === 'totalRating') {
                        acc.push(totalRatingV2);
                        return acc;
                    }
                }

                if (column.key === 'electricForesightedDrivingPercentage') {
                    acc.push({
                        ...electricForesightedDrivingPercentage,
                        ...replacements.electricForesightedDrivingPercentage,
                    });
                    return acc;
                }
                if (column.key === 'electricCruiseControlRating') {
                    acc.push({
                        ...electricCruiseControlRating,
                        ...replacements.electricCruiseControlRating,
                    });
                    return acc;
                }
                if (column.key === 'electricOverspeedRating') {
                    acc.push({
                        ...electricOverspeedRating,
                        ...replacements.electricOverspeedRating,
                    });
                    return acc;
                }
                if (column.key === 'electricAccelerationPedalRating') {
                    acc.push({
                        ...electricAccelerationPedalRating,
                        ...replacements.electricAccelerationPedalRating,
                    });
                    return acc;
                }
                acc.push(column);

                return acc;
            }, []),
            columns: config.columns.reduce((acc: Column[], column: Column) => {
                if (
                    !shouldShowColumn(
                        column,
                        calculationParams,
                        selectedFuelType,
                        shouldShowHarshKPIs,
                        shouldShowExcessiveKPIs,
                        shouldConsiderElectricParams,
                    )
                ) {
                    return acc;
                }

                if (shouldUseV3Ratings) {
                    if (column.key === 'accelerationPedalRating') {
                        acc.push({
                            ...accelerationPedalRating,
                            ...replacements.accelerationPedalRating,
                        });
                        return acc;
                    }

                    if (column.key === 'cruiseControlRating') {
                        acc.push({
                            ...cruiseControlRatingV3,
                            ...replacements.cruiseControlRatingV3,
                        });
                        return acc;
                    }

                    if (column.key === 'totalRating') {
                        acc.push({ ...totalRatingV3, ...replacements.totalRatingV3 });
                        return acc;
                    }
                } else if (shouldUseV2Ratings) {
                    if (column.key === 'accelerationPedalRating') {
                        acc.push({
                            ...accelerationPedalRatingV2,
                            ...replacements.accelerationPedalRatingV2,
                        });
                        return acc;
                    }

                    if (column.key === 'cruiseControlRating') {
                        acc.push({
                            ...cruiseControlRatingV2,
                            ...replacements.cruiseControlRatingV2,
                        });

                        return acc;
                    }
                }

                if (column.key === 'electricCruiseControlRating') {
                    acc.push({
                        ...electricCruiseControlRating,
                        ...replacements.electricCruiseControlRating,
                    });
                    return acc;
                }
                if (column.key === 'electricOverspeedRating') {
                    acc.push({
                        ...electricOverspeedRating,
                        ...replacements.electricOverspeedRating,
                    });
                    return acc;
                }

                if (
                    config.key === 'overview' &&
                    shouldExcludeOverviewSOPColumn(column, selectedFuelType)
                ) {
                    return acc;
                }

                if (config.key === 'ratings' && column.key === 'totalRating') {
                    if (
                        selectedFuelType === DriverFuelType.ALL ||
                        selectedFuelType === DriverFuelType.FUEL
                    ) {
                        acc.push({
                            ...fuelForesightedDrivingPercentage,
                        });
                    }

                    if (
                        selectedFuelType === DriverFuelType.ALL ||
                        selectedFuelType === DriverFuelType.ELECTRIC
                    ) {
                        acc.push({
                            ...electricForesightedDrivingPercentage,
                        });
                    }
                    return acc;
                }

                if (column.key === 'electricForesightedDrivingPercentage') {
                    acc.push({
                        ...electricForesightedDrivingPercentage,
                    });
                    return acc;
                }

                if (column.key === 'electricAccelerationPedalRating') {
                    acc.push({
                        ...electricAccelerationPedalRating,
                        ...replacements.electricAccelerationPedalRating,
                    });
                    return acc;
                }

                if (
                    selectedFuelType === DriverFuelType.FUEL &&
                    column.key === 'electricRecuperationRating'
                ) {
                    return acc;
                }

                if (column.key === 'electricRecuperationRating') {
                    acc.push({
                        ...electricRecuperationRating,
                        ...replacements.electricRecuperationRating,
                    });
                    return acc;
                }

                acc.push(column);

                return acc;
            }, []),
        };
    }

    return;
};
